/**
 * FormFooter Component
 * Footer with action buttons for request form
 */

import React, { memo, MouseEventHandler } from 'react';
import { Button } from '@dynamon-as/ui';
import { Edit, Send, Cancel } from '@mui/icons-material';
import { Tooltip as MUITooltip } from '@mui/material';
import type {
    CreateRequestDto,
    UpdateRequestDto,
} from '@features/requests/types';
import { useI18n } from '@shared/i18n';
import styles from './FormFooter.module.css';

/**
 * Props for FormFooter component
 */
export interface FormFooterProps {
    /** Whether the form is in create mode */
    isCreate: boolean;
    /** Callback when cancel button is clicked */
    onCancel: () => void;
    /** Callback when save/update button is clicked */
    onSubmit: (dto: CreateRequestDto | UpdateRequestDto) => void;
    /** Callback when submit action button is clicked (only for create mode) */
    onSubmitAction?: (dto: CreateRequestDto | UpdateRequestDto) => void;
    /** Function to validate the form */
    validateForm: () => boolean;
    /** Function to build the DTO from form data */
    buildDto: () => CreateRequestDto | UpdateRequestDto;
}

/**
 * Helper function to handle form submission with validation
 */
const createSubmitHandler = (
    validateForm: () => boolean,
    buildDto: () => CreateRequestDto | UpdateRequestDto,
    onSubmit: (dto: CreateRequestDto | UpdateRequestDto) => void,
    onSubmitAction?: (dto: CreateRequestDto | UpdateRequestDto) => void,
): {
    handleSubmit: MouseEventHandler<HTMLButtonElement>;
    handleSubmitAction: MouseEventHandler<HTMLButtonElement>;
} => {
    const handleSubmit: MouseEventHandler<HTMLButtonElement> = (): void => {
        if (validateForm()) {
            onSubmit(buildDto());
        }
    };

    const handleSubmitAction: MouseEventHandler<
        HTMLButtonElement
    > = (): void => {
        if (validateForm()) {
            onSubmitAction?.(buildDto());
        }
    };

    return { handleSubmit, handleSubmitAction };
};

/**
 * Tooltip styling aligned with Dynamon design tokens
 */
const tooltipStyle = {
    backgroundColor: 'var(--dyn-color-white)',
    color: 'var(--dyn-color-black-2)',
    border: '1px solid var(--dyn-color-gray-4)',
    fontSize: 'var(--dyn-text-sm-font-size)',
    borderRadius: 'var(--dyn-radius-sm)',
    padding: 'var(--dyn-space-1) var(--dyn-space-2)',
};

/**
 * FormFooter component - Memoized to prevent unnecessary re-renders
 */
export function FormFooter({
    isCreate,
    onCancel,
    onSubmit,
    onSubmitAction,
    validateForm,
    buildDto,
}: FormFooterProps): React.ReactElement {
    const { t } = useI18n();
    const { handleSubmit, handleSubmitAction } = createSubmitHandler(
        validateForm,
        buildDto,
        onSubmit,
        onSubmitAction,
    );

    const isFormValid = validateForm();

    return (
        <div className={styles.footer}>
            <Button
                text={t('common.cancel')}
                color="secondary"
                variant="outlined"
                onClick={onCancel}
                type="button"
                Icon={(): React.ReactElement => <Cancel fontSize="small" />}
                aria-label={t('requests.form.actions.cancel')}
                data-testid="form-cancel-button"
            />
            <Button
                text={
                    isCreate
                        ? t('requests.form.actions.saveDraft')
                        : t('requests.form.actions.update')
                }
                color="primary"
                variant="normal"
                type="button"
                onClick={handleSubmit}
                Icon={(): React.ReactElement =>
                    isCreate ? (
                        <Edit fontSize="small" />
                    ) : (
                        <Edit fontSize="small" />
                    )
                }
                aria-label={
                    isCreate
                        ? t('requests.form.actions.saveDraft')
                        : t('requests.form.actions.update')
                }
                data-testid="form-save-button"
            />
            {isCreate && onSubmitAction && (
                <MUITooltip
                    title={
                        !isFormValid
                            ? t('requests.form.submitDisabledNoDates')
                            : ''
                    }
                    arrow
                    slotProps={{ tooltip: { style: tooltipStyle } }}
                >
                    <span>
                        <Button
                            text={t('requests.form.actions.submit')}
                            color="success"
                            variant="normal"
                            type="button"
                            onClick={handleSubmitAction}
                            Icon={(): React.ReactElement => (
                                <Send fontSize="small" />
                            )}
                            aria-label={t('requests.form.actions.submit')}
                            data-testid="form-submit-button"
                            disabled={!isFormValid}
                        />
                    </span>
                </MUITooltip>
            )}
        </div>
    );
}

export default memo(FormFooter);
